import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/utils";

export type PlainPanelSurface = "white" | "sand";

const SURFACE: Record<PlainPanelSurface, string> = {
  /** A card on the cream page: white with a hairline. Padding is the caller's. */
  white:
    "relative overflow-hidden rounded-3xl border border-caire-hairline bg-caire-surface transition-transform duration-200 motion-reduce:transition-none",
  /** A lifted section panel on the cream page; cards inside it sit on white. */
  sand: "rounded-3xl bg-caire-sand p-5 sm:p-7",
};

const HOVER =
  "group hover:-translate-y-[3px] motion-reduce:hover:translate-y-0";

/**
 * Classes for a plain panel, for markup that cannot take the component (a
 * `Link`, a `details`). `interactive` adds the hover lift and `group`, and only
 * on a white card: a `group` on a container would fire every nested card's
 * `group-hover` at once.
 */
export function plainPanelClass(
  surface: PlainPanelSurface = "white",
  interactive = false,
): string {
  return interactive && surface === "white"
    ? cn(HOVER, SURFACE.white)
    : SURFACE[surface];
}

export interface PlainPanelProps extends HTMLAttributes<HTMLElement> {
  surface?: PlainPanelSurface;
  interactive?: boolean;
  as?: "div" | "section" | "article" | "li";
  children?: ReactNode;
}

/**
 * The plain panel (Brand 2.2): no domain, no icon, just a surface. Use a
 * `DomainCard` or `DomainSection` when the panel holds one kind of
 * information; this is for neutral grouping.
 */
export function PlainPanel({
  surface = "white",
  interactive = false,
  as: Tag = "div",
  className,
  children,
  ...rest
}: PlainPanelProps) {
  return (
    <Tag
      className={cn(plainPanelClass(surface, interactive), className)}
      {...rest}
    >
      {children}
    </Tag>
  );
}
